import { Form, Input, Tooltip, Typography } from 'antd';
import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceIdentification } from '../../IdentifyDevice.types';
import { FieldTooltipContent } from '../FieldTooltipContent/FieldTooltipContent';
import { FieldTooltipGlobalStyle } from '../FieldTooltipContent/FieldTooltipContent.styles';
import { StyledDeviceIdentificationForm } from './DeviceIdentificationForm.styles';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import materialNumberImg from 'app/assets/images/service/material-number.png';
import serialNumberImg from 'app/assets/images/service/serial-number.jpg';
import { MATERIAL_NUMBER_PATTERN, SERIAL_NUMBER_LENGTH } from 'app/modules/service/utils/constants';
import { formatMaterialNumber } from 'app/modules/service/utils/device';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { useServiceRequestContext } from 'app/modules/service/context/ServiceContext';

interface DeviceIdentificationFormProps {
  initialValues?: DeviceIdentification;
  onValidityChange: (isValid: boolean) => void;
  showHelpLink?: boolean;
  onHelpLinkClick?: () => void;
  footer?: ReactNode;
}

export const DeviceIdentificationForm = ({
  initialValues,
  onValidityChange,
  showHelpLink,
  onHelpLinkClick,
  footer,
}: DeviceIdentificationFormProps): JSX.Element => {
  const { t } = useTranslation();
  const [form] = Form.useForm<DeviceIdentification>();

  const { handleDevice } = useServiceRequestContext();

  const materialNumber: string = Form.useWatch('materialNumber', form) ?? '';
  const serialNumber: string = Form.useWatch('serialNumber', form) ?? '';

  const isMaterialNumberValid = MATERIAL_NUMBER_PATTERN.test(materialNumber);
  const isSerialNumberValid = serialNumber.length === SERIAL_NUMBER_LENGTH;

  useEffect(() => {
    onValidityChange(isMaterialNumberValid && isSerialNumberValid);

    if (isMaterialNumberValid && isSerialNumberValid) {
      form.submit();
    }
  }, [isMaterialNumberValid, isSerialNumberValid, onValidityChange, form]);

  const onFinish = (values: DeviceIdentification): void => {
    handleDevice(values);
  };

  return (
    <StyledDeviceIdentificationForm>
      <FieldTooltipGlobalStyle />
      <Form form={form} className="device-form__content" onFinish={onFinish} initialValues={initialValues}>
        <div className="device-form__field device-form__field--material-number">
          <label className="device-form__label">
            {t('service.identifyDevice.form.materialNo.label')}
            <Tooltip
              placement="bottomLeft"
              arrow={{ pointAtCenter: true }}
              title={
                <FieldTooltipContent
                  img={materialNumberImg}
                  alt="Material number location"
                  text={t('service.identifyDevice.form.materialNo.tooltip')}
                />
              }
              color={kccThemeConstants.COLORS.BLACK}
              rootClassName="field-tooltip"
            >
              <span className="device-form__info-icon">
                <SvgIcon name="information" />
              </span>
            </Tooltip>
          </label>
          <Form.Item
            name="materialNumber"
            validateTrigger="onBlur"
            normalize={(value: string): string => formatMaterialNumber(value.replace(/\D/g, ''))}
            rules={[
              {
                required: true,
                message: t('service.identifyDevice.form.materialNo.error'),
              },
              {
                pattern: MATERIAL_NUMBER_PATTERN,
                message: t('service.identifyDevice.form.materialNo.error'),
              },
            ]}
          >
            <Input
              className={`device-form__input${isMaterialNumberValid ? ' device-form__input--valid' : ''}`}
              placeholder="0.000-000.0"
              suffix={isMaterialNumberValid ? <SvgIcon name="check" className="device-form__check-icon" /> : <span />}
            />
          </Form.Item>
        </div>

        <div className="device-form__field">
          <label className="device-form__label">
            {t('service.identifyDevice.form.serialNo.label')}
            <Tooltip
              placement="bottomLeft"
              arrow={{ pointAtCenter: true }}
              color={kccThemeConstants.COLORS.BLACK}
              title={
                <FieldTooltipContent
                  img={serialNumberImg}
                  alt="Serial number location"
                  text={t('service.identifyDevice.form.serialNo.tooltip')}
                />
              }
              rootClassName="field-tooltip"
            >
              <span className="device-form__info-icon">
                <SvgIcon name="information" />
              </span>
            </Tooltip>
          </label>
          <Form.Item
            name="serialNumber"
            validateTrigger="onBlur"
            normalize={(value: string): string => value.replace(/\D/g, '').slice(0, SERIAL_NUMBER_LENGTH)}
            rules={[
              {
                required: true,
                message: t('service.identifyDevice.form.serialNo.error'),
              },
              {
                len: SERIAL_NUMBER_LENGTH,
                message: t('service.identifyDevice.form.serialNo.error'),
              },
            ]}
          >
            <Input
              className={`device-form__input${isSerialNumberValid ? ' device-form__input--valid' : ''}`}
              placeholder="000000"
              suffix={isSerialNumberValid ? <SvgIcon name="check" className="device-form__check-icon" /> : <span />}
            />
          </Form.Item>
        </div>

        {showHelpLink && (
          <Typography.Link className="device-form__help-link" onClick={onHelpLinkClick}>
            {t('service.identifyDevice.form.helpLink')}
          </Typography.Link>
        )}

        {footer}
      </Form>
    </StyledDeviceIdentificationForm>
  );
};
